/**
 * 盤の座標ラベル (筋 / 段) を **盤面の外側** — 木地や縁の余白 — に置くレイヤ。
 *
 * 以前は端の升 / 交点の中に `<span>` を入れていたが、 そこには駒や石が載るので
 * 文字と重なって読めなかった (9 路の囲碁が最悪で、 隅は数字と英字も重なる)。
 * 実物の盤と同じく、 線の外に出す。
 *
 * - `variant='cell'`      升の中央に合わせる (チェス・将棋・リバーシ 等)
 * - `variant='intersection'` 交点に合わせる (囲碁・連珠・コネクト6)
 *
 * ラベルは **表示順で** 受け取る。 盤を反転して描く側 (= 後手視点) が並べ替えた
 * 配列をそのまま渡せば、 ラベルも一緒に反転する。
 *
 * 置き場所 (盤のどれだけ外側か) は CSS 側で盤ごとに決める。 レイヤの箱を升目の
 * 領域にぴったり合わせておけば、 中の % 位置はこの component だけで完結する。
 */
import type { CSSProperties } from 'react';

export function BoardCoords({
  files,
  ranks,
  variant = 'cell',
}: {
  /** 左から右の順に並べた筋ラベル。 */
  files: string[];
  /** 上から下の順に並べた段ラベル。 */
  ranks: string[];
  variant?: 'cell' | 'intersection';
}) {
  const pos = (i: number, n: number) =>
    variant === 'cell' ? ((i + 0.5) / n) * 100 : n > 1 ? (i / (n - 1)) * 100 : 50;
  // 交点式の盤では、 端の線に載った石が線の外へ半升ぶんはみ出す。 その分だけ
  // ラベルを外へ逃がす (升の中に収まる駒しか無い升式の盤では 0 で良い)。
  const out =
    variant === 'intersection' && ranks.length > 1 ? (45 / (ranks.length - 1)).toFixed(3) : '0';
  return (
    <div className="board-coords" aria-hidden style={{ '--coord-out': `${out}%` } as CSSProperties}>
      {files.map((f, i) => (
        <span
          // ラベルは重複しうる (連珠の A..O は一意だが、 汎用なので index を混ぜる)。
          key={`f${i}-${f}`}
          className="board-coord board-coord--file"
          style={{ left: `${pos(i, files.length)}%` }}
        >
          {f}
        </span>
      ))}
      {ranks.map((r, i) => (
        <span
          key={`r${i}-${r}`}
          className="board-coord board-coord--rank"
          style={{ top: `${pos(i, ranks.length)}%` }}
        >
          {r}
        </span>
      ))}
    </div>
  );
}
